import classNames from "classnames";
import map from "lodash/map";
import { ActionIcon } from "PFComponents/action_icon";
import { Alert } from "PFComponents/alert";
import { Button, ButtonKind } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import { Tooltip } from "PFComponents/tooltip";
import { Typography } from "PFComponents/typography";
import { IconName } from "PFTheme/graphics/icons";
import React, { ReactNode } from "react";

import css from "./subsidiary_mode_alert.module.scss";

export type Action = {
  icon?: IconName;
  onClick?: () => void;
  disabled?: boolean;
  title?: ReactNode;
  label?: string;
  ariaLabel?: string;
  buttonKind?: ButtonKind;
  node?: ReactNode;
  endAdornment?: ReactNode;
  qaId?: string;
};

type SubsidiaryModeAlertProps = {
  className?: string;
  title: string | React.ReactNode;
  actions?: Action[];
  onClose: () => void;
  closeQaId?: string;
};

const ActionButton = ({
  icon,
  buttonKind,
  onClick,
  disabled,
  title,
  label,
  ariaLabel,
  node,
  endAdornment,
  qaId
}: Action) => {
  if (node) {
    return node;
  }

  const button = endAdornment ? (
    <Button
      kind={buttonKind ?? "ghost"}
      className={css.endAdornmentButton}
      aria-label={ariaLabel}
      onClick={onClick}
      disabled={disabled}
      qaId={qaId}
    >
      {icon && <Icon name={icon} size="sm" />}
      {label && <span>{label}</span>}
      {endAdornment}
    </Button>
  ) : (
    <Button
      kind={buttonKind ?? "ghost"}
      text={label}
      aria-label={ariaLabel}
      icon={icon}
      onClick={onClick}
      disabled={disabled}
      qaId={qaId}
    />
  );

  return title ? <Tooltip content={title}>{button}</Tooltip> : button;
};

export const SubsidiaryModeAlert = ({
  className,
  title,
  actions,
  onClose,
  closeQaId
}: SubsidiaryModeAlertProps) => (
  <Alert className={classNames(css.content, className)} hideIcon kind="info">
    <ActionIcon name="cross" onClick={onClose} disableHover qaId={closeQaId} />
    <Typography variant="bodyRegular">{title}</Typography>
    {actions && (
      <span className={css.actions}>
        {map(actions, (action, index) => (
          <ActionButton key={index} {...action} />
        ))}
      </span>
    )}
  </Alert>
);
